<template>
  <!--
    와이드 화면: 문의 | 후기 이중 레인(비슷한 CS 기능을 한 화면에).
    좁은 화면: 탭 + 목록/상세 스택.
  -->
  <div class="srev">
    <section class="sui-card srev__kpis" data-tour="seller-reviews-kpis" aria-label="리뷰·문의 요약">
      <div class="srev__kpi srev__kpi--score">
        <p class="srev__score-value">{{ avgRating }}</p>
        <p class="srev__stars">
          <AppIcon
            v-for="star in 5"
            :key="star"
            :icon="star <= Math.round(Number(avgRating)) ? 'mdi-star' : 'mdi-star-outline'"
            :size="15"
          />
        </p>
        <p class="srev__kpi-label">평균 별점 · 후기 {{ reviews.length }}</p>
      </div>
      <div class="srev__kpi-dist">
        <ul class="srev__dist">
          <li v-for="row in distribution" :key="row.score">
            <span>{{ row.score }}</span>
            <em><i :style="{ width: `${row.percent}%` }" /></em>
            <b>{{ row.count }}</b>
          </li>
        </ul>
      </div>
      <button type="button" class="srev__kpi" :class="{ 'is-active': focusLane === 'inquiry' && inquiryFilter === 'open' }" @click="focusOpenInquiries">
        <p class="srev__kpi-value is-warn">{{ openInquiries }}<em>건</em></p>
        <p class="srev__kpi-label">미답변 문의</p>
      </button>
      <button type="button" class="srev__kpi" :class="{ 'is-active': focusLane === 'review' && reviewFilter === 'open' }" @click="focusOpenReviews">
        <p class="srev__kpi-value is-warn">{{ unrepliedReviews }}<em>건</em></p>
        <p class="srev__kpi-label">답변 대기 후기</p>
      </button>
      <div class="srev__kpi">
        <p class="srev__kpi-value">{{ mergedInquiries.length }}<em>건</em></p>
        <p class="srev__kpi-label">전체 문의</p>
      </div>
    </section>

    <section class="sui-card srev__toolbar-card">
      <div class="sui-toolbar srev__toolbar" data-tour="seller-reviews-toolbar">
        <div class="sui-presets" role="group" aria-label="기간">
          <button
            v-for="item in presets"
            :key="item.key"
            type="button"
            :class="{ 'is-active': preset === item.key }"
            @click="applyPreset(item.key)"
          >
            {{ item.label }}
          </button>
        </div>
        <label class="sui-date">
          <span>시작일</span>
          <input v-model="fromDate" type="date" @change="onManualDateChange" />
        </label>
        <label class="sui-date">
          <span>종료일</span>
          <input v-model="toDate" type="date" @change="onManualDateChange" />
        </label>
        <label class="sui-search">
          <AppIcon icon="mdi-magnify" :size="17" />
          <input v-model.trim="keyword" type="search" placeholder="상품명 · 주문번호 · 내용 검색" />
        </label>
        <span class="sui-toolbar__spacer" />
        <button
          type="button"
          class="sui-btn"
          data-tour="seller-reviews-templates"
          @click="openTemplates"
        >
          <AppIcon icon="mdi-text-box-edit-outline" :size="16" />
          템플릿 관리
        </button>
        <button type="button" class="sui-btn" :disabled="loading" @click="load">
          <AppIcon icon="mdi-refresh" :size="16" />
          새로고침
        </button>
      </div>
      <div v-if="productCode || productFilterLabel" class="srev__chips">
        <span class="srev__chip">
          상품 필터
          <strong>{{ productFilterLabel || productCode }}</strong>
          <button type="button" aria-label="상품 필터 해제" @click="clearProductFilter">
            <AppIcon icon="mdi-close" :size="14" />
          </button>
        </span>
      </div>
      <p v-if="errorMessage" class="srev__error">
        <AppIcon icon="mdi-alert-outline" :size="16" />
        {{ errorMessage }}
      </p>
    </section>

    <!-- 모바일 탭 -->
    <div class="srev__mobile-tabs" role="tablist">
      <button
        type="button"
        role="tab"
        class="srev__mobile-tab"
        :class="{ 'is-active': mobileTab === 'inquiry' }"
        @click="mobileTab = 'inquiry'"
      >
        상품 문의 <em>{{ filteredInquiries.length }}</em>
      </button>
      <button
        type="button"
        role="tab"
        class="srev__mobile-tab"
        :class="{ 'is-active': mobileTab === 'review' }"
        @click="mobileTab = 'review'"
      >
        상품 후기 <em>{{ filteredReviews.length }}</em>
      </button>
    </div>

    <div class="srev__workbench" data-tour="seller-reviews-lanes">
      <!-- 문의 레인 -->
      <section
        class="sui-card srev__lane"
        :class="{ 'is-hidden-mobile': mobileTab !== 'inquiry' }"
      >
        <header class="srev__lane-head">
          <h2>
            상품 문의
            <em>{{ filteredInquiries.length }}</em>
          </h2>
          <div class="srev__lane-filters">
            <button
              v-for="opt in laneFilters"
              :key="`q-${opt.key}`"
              type="button"
              :class="{ 'is-active': inquiryFilter === opt.key }"
              @click="inquiryFilter = opt.key"
            >
              {{ opt.label }}
            </button>
          </div>
        </header>

        <div class="srev__lane-body">
          <ul class="srev__queue">
            <li
              v-for="inquiry in filteredInquiries"
              :key="inquiry.key"
              class="srev__row"
              :class="[
                inquirySlaClass(inquiry),
                { 'is-selected': selectedInquiryId === inquiry.key, 'is-open': !inquiry.answer },
              ]"
              @click="selectInquiry(inquiry.key)"
            >
              <div class="srev__row-top">
                <span class="sui-badge" :class="inquiry.answer ? 'sui-badge--green' : 'sui-badge--red'">
                  {{ inquiry.answer ? '완료' : '미답변' }}
                </span>
                <span
                  class="srev__source-chip"
                  :class="`is-${inquiry.source}`"
                  :title="inquiry.source === 'consult' ? (inquiry.sellerCanReply ? '관리자가 답변 권한을 위임한 1:1 상담. 판매업체가 직접 답변할 수 있습니다.' : '관리자가 답변하는 1:1 상담. 판매업체는 조회만 합니다.') : '판매업체가 직접 답변하는 상품문의'"
                >
                  {{ inquiry.source === 'consult' ? '1:1 상담' : '상품문의' }}
                </span>
                <span
                  v-if="inquiry.source === 'consult' && inquiry.sellerCanReply"
                  class="srev__delegate-chip"
                  title="관리자가 이 상담의 답변 권한을 판매업체에게 위임했습니다"
                >
                  <AppIcon icon="mdi-shield-check-outline" :size="12" />
                  답변 위임
                </span>
                <router-link
                  v-if="inquiry.orderCode"
                  class="srev__order-chip"
                  :to="{ path: '/seller/orders', query: { keyword: inquiry.orderCode } }"
                  :title="`${inquiry.orderCode} 주문으로 이동`"
                  @click.stop
                >
                  {{ inquiry.orderCode }}
                </router-link>
                <span v-else class="srev__order-chip is-none">일반 문의</span>
                <span v-if="inquirySlaLabel(inquiry)" class="srev__sla" :class="inquirySlaClass(inquiry)">
                  {{ inquirySlaLabel(inquiry) }}
                </span>
                <span v-if="inquiry.secret" class="sui-badge sui-badge--gray">비밀</span>
                <time>{{ inquiry.date }}</time>
              </div>
              <p class="srev__row-product">{{ inquiry.productName }}</p>
              <p class="srev__row-preview">{{ inquiry.question }}</p>
            </li>
            <li v-if="!filteredInquiries.length" class="srev__queue-empty">
              {{ loading ? '불러오는 중…' : '조건에 맞는 문의가 없습니다.' }}
            </li>
          </ul>

          <aside v-if="selectedInquiry" class="srev__detail">
            <div class="srev__detail-head">
              <span class="sui-badge" :class="selectedInquiry.answer ? 'sui-badge--green' : 'sui-badge--red'">
                {{ selectedInquiry.answer ? '답변 완료' : '미답변' }}
              </span>
              <span
                class="srev__source-chip"
                :class="`is-${selectedInquiry.source}`"
              >
                {{
                  selectedInquiry.source === 'consult'
                    ? selectedInquiry.sellerCanReply
                      ? '1:1 상담 (답변 위임)'
                      : '1:1 상담 (관리자 응대)'
                    : '상품문의'
                }}
              </span>
              <span
                v-if="selectedInquiry.source === 'consult' && selectedInquiry.sellerCanReply"
                class="srev__delegate-chip"
              >
                <AppIcon icon="mdi-shield-check-outline" :size="12" />
                답변 위임
              </span>
              <p>{{ selectedInquiry.author || '작성자' }} · {{ selectedInquiry.org }} · {{ selectedInquiry.date }}</p>
            </div>
            <p class="srev__detail-product">{{ selectedInquiry.productName }}</p>
            <p class="srev__detail-body">{{ selectedInquiry.question }}</p>

            <div v-if="selectedInquiry.answer" class="srev__reply">
              <AppIcon icon="mdi-subdirectory-arrow-right" :size="16" />
              <p>
                {{ selectedInquiry.answer }}
                <em v-if="selectedInquiry.answeredAt">
                  {{ selectedInquiry.answeredAt }}
                  <template v-if="selectedInquiry.answeredBy"> · {{ selectedInquiry.answeredBy }}</template>
                </em>
              </p>
            </div>
            <!-- 상품문의는 미답변만, 1:1 상담은 위임되면 답변완료 후에도 추가 답변 가능 -->
            <form
              v-if="canWriteInquiryReply(selectedInquiry)"
              class="srev__form"
              @submit.prevent="submitAnswer(selectedInquiry)"
            >
              <p
                v-if="selectedInquiry.source === 'consult'"
                class="srev__consult-note is-delegated"
              >
                <AppIcon icon="mdi-shield-check-outline" :size="16" />
                {{
                  selectedInquiry.answer
                    ? '관리자가 답변 권한을 위임했습니다. 이미 답변이 있어도 추가로 남길 수 있고, 「판매자」로 기록됩니다.'
                    : '관리자가 이 1:1 상담의 답변 권한을 위임했습니다. 답변은 즉시 상담 스레드에 「판매자」로 기록됩니다.'
                }}
              </p>
              <div class="srev__templates" role="group" aria-label="답변 템플릿">
                <button
                  v-for="tpl in qnaTemplates"
                  :key="`q-${tpl}`"
                  type="button"
                  class="srev__tpl"
                  @click="applyTemplate(selectedInquiry.key, tpl)"
                >
                  {{ tpl }}
                </button>
                <button
                  type="button"
                  class="srev__tpl srev__tpl--manage"
                  @click="openTemplates"
                >
                  템플릿 관리
                </button>
              </div>
              <textarea
                v-model.trim="drafts[selectedInquiry.key]"
                class="sui-input srev__textarea"
                rows="5"
                :placeholder="inquiryReplyPlaceholder(selectedInquiry)"
              />
              <button
                type="submit"
                class="sui-btn sui-btn--primary"
                :disabled="!drafts[selectedInquiry.key] || working === selectedInquiry.key"
              >
                {{
                  working === selectedInquiry.key
                    ? '등록 중…'
                    : selectedInquiry.source === 'consult' && selectedInquiry.answer
                      ? '추가 답변 등록'
                      : '답변 등록'
                }}
              </button>
            </form>
            <p
              v-else-if="selectedInquiry.source === 'consult' && !selectedInquiry.answer"
              class="srev__consult-note"
            >
              <AppIcon icon="mdi-information-outline" :size="16" />
              1:1 상담은 함우리몰 관리자가 대신 응대합니다. 판매업체는 참고 목적으로 열람만 가능하고, 답변이 필요하면 관리자에게 위임을 요청해 주세요.
            </p>
          </aside>
          <aside v-else class="srev__detail srev__detail--empty">
            <AppIcon icon="mdi-forum-outline" :size="36" />
            <p>왼쪽에서 문의를 선택하면<br />여기에서 답변합니다.</p>
          </aside>
        </div>
      </section>

      <!-- 후기 레인 -->
      <section
        class="sui-card srev__lane"
        :class="{ 'is-hidden-mobile': mobileTab !== 'review' }"
      >
        <header class="srev__lane-head">
          <h2>
            상품 후기
            <em>{{ filteredReviews.length }}</em>
          </h2>
          <div class="srev__lane-filters">
            <button
              v-for="opt in laneFilters"
              :key="`r-${opt.key}`"
              type="button"
              :class="{ 'is-active': reviewFilter === opt.key }"
              @click="reviewFilter = opt.key"
            >
              {{ opt.label }}
            </button>
          </div>
        </header>

        <div class="srev__lane-body">
          <ul class="srev__queue">
            <li
              v-for="review in filteredReviews"
              :key="review.id"
              class="srev__row"
              :class="{ 'is-selected': selectedReviewId === review.id, 'is-open': !review.reply }"
              @click="selectReview(review.id)"
            >
              <div class="srev__row-top">
                <span class="srev__row-stars">
                  <AppIcon
                    v-for="star in 5"
                    :key="star"
                    :icon="star <= review.rating ? 'mdi-star' : 'mdi-star-outline'"
                    :size="12"
                  />
                </span>
                <span class="sui-badge" :class="review.reply ? 'sui-badge--green' : 'sui-badge--amber'">
                  {{ review.reply ? '완료' : '대기' }}
                </span>
                <router-link
                  v-if="review.orderCode"
                  class="srev__order-chip"
                  :to="{ path: '/seller/orders', query: { keyword: review.orderCode } }"
                  :title="`${review.orderCode} 주문으로 이동`"
                  @click.stop
                >
                  {{ review.orderCode }}
                </router-link>
                <span v-else class="srev__order-chip is-none">주문 없음</span>
                <time>{{ review.date }}</time>
              </div>
              <p class="srev__row-product">{{ review.productName }}</p>
              <p class="srev__row-preview">{{ review.content }}</p>
            </li>
            <li v-if="!filteredReviews.length" class="srev__queue-empty">
              {{ loading ? '불러오는 중…' : '조건에 맞는 후기가 없습니다.' }}
            </li>
          </ul>

          <aside v-if="selectedReview" class="srev__detail">
            <div class="srev__detail-head">
              <span class="srev__row-stars">
                <AppIcon
                  v-for="star in 5"
                  :key="star"
                  :icon="star <= selectedReview.rating ? 'mdi-star' : 'mdi-star-outline'"
                  :size="14"
                />
              </span>
              <p>{{ selectedReview.author }} · {{ selectedReview.org }} · {{ selectedReview.date }}</p>
            </div>
            <p class="srev__detail-product">{{ selectedReview.productName }}</p>
            <p class="srev__detail-body">{{ selectedReview.content }}</p>

            <div v-if="selectedReview.reply" class="srev__reply">
              <AppIcon icon="mdi-subdirectory-arrow-right" :size="16" />
              <p>{{ selectedReview.reply }}</p>
            </div>
            <form v-else class="srev__form" @submit.prevent="submitReply(selectedReview)">
              <div class="srev__templates" role="group" aria-label="답변 템플릿">
                <button
                  v-for="tpl in replyTemplates"
                  :key="`r-${tpl}`"
                  type="button"
                  class="srev__tpl"
                  @click="applyTemplate(selectedReview.id, tpl)"
                >
                  {{ tpl }}
                </button>
                <button
                  type="button"
                  class="srev__tpl srev__tpl--manage"
                  @click="openTemplates"
                >
                  템플릿 관리
                </button>
              </div>
              <textarea
                v-model.trim="drafts[selectedReview.id]"
                class="sui-input srev__textarea"
                rows="5"
                placeholder="구매 기관에 남길 답변을 작성하세요."
              />
              <button
                type="submit"
                class="sui-btn sui-btn--primary"
                :disabled="!drafts[selectedReview.id] || working === selectedReview.id"
              >
                {{ working === selectedReview.id ? '등록 중…' : '답변 등록' }}
              </button>
            </form>
          </aside>
          <aside v-else class="srev__detail srev__detail--empty">
            <AppIcon icon="mdi-comment-outline" :size="36" />
            <p>왼쪽에서 후기를 선택하면<br />여기에서 답변합니다.</p>
          </aside>
        </div>
      </section>
    </div>

    <transition name="sui-toast">
      <p v-if="toast" class="sui-toast">
        <AppIcon icon="mdi-check-circle" :size="18" />
        {{ toast }}
      </p>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  answerSellerConsult,
  answerSellerQna,
  fetchSellerConsults,
  fetchSellerQnas,
  fetchSellerReviews,
  replySellerReview,
  type MallQna,
  type SellerMallReview,
  type SellerSharedConsult,
} from '@/api/mallReview'
import { useListDateRange } from '@/composables/useListDateRange'

const route = useRoute()
const router = useRouter()

const reviews = ref<SellerMallReview[]>([])
const inquiries = ref<MallQna[]>([])
/**
 * 관리자 1:1 상담(caion_consult_req) 중 내가 판 주문에 걸린 항목.
 * 판매업체는 조회만 가능하고 답변은 관리자만 등록한다.
 */
const consults = ref<SellerSharedConsult[]>([])
const loading = ref(false)
const errorMessage = ref('')
const working = ref('')
const toast = ref('')
let toastTimer: ReturnType<typeof setTimeout> | undefined

const mobileTab = ref<'review' | 'inquiry'>('inquiry')
const focusLane = ref<'review' | 'inquiry' | null>(null)
const reviewFilter = ref<'all' | 'open' | 'done'>('all')
const inquiryFilter = ref<'all' | 'open' | 'done'>('open')
const keyword = ref('')
const productCode = ref('')
const drafts = reactive<Record<string, string>>({})
const selectedReviewId = ref('')
const selectedInquiryId = ref('')
const { fromDate, toDate, preset, presets, applyPreset, onManualDateChange, inRange } =
  useListDateRange('3m')

function applyRouteQuery() {
  const tab = String(route.query.tab || '')
  mobileTab.value = tab === 'review' ? 'review' : 'inquiry'
  focusLane.value = mobileTab.value

  const filter = String(route.query.filter || '')
  if (filter === 'open' || filter === 'done' || filter === 'all') {
    if (mobileTab.value === 'review') reviewFilter.value = filter
    else inquiryFilter.value = filter
  } else if (mobileTab.value === 'inquiry' && !route.query.filter) {
    inquiryFilter.value = 'open'
  }

  keyword.value = String(route.query.keyword || '').trim()
  productCode.value = String(route.query.productCode || route.query.product || '').trim()

  /*
   * 관리 주문 화면·알림·CS 링크에서 특정 주문번호·상품으로 좁혀 넘어오면 3개월 기본 필터에 걸려
   * 빈 화면이 뜰 수 있다. 필터 파라미터가 있으면 「전체」기간으로 자동 확장한다.
   */
  if (productCode.value || keyword.value) {
    applyPreset('all')
  }
}

applyRouteQuery()

watch(
  () => [route.query.tab, route.query.filter, route.query.keyword, route.query.productCode, route.query.product],
  () => {
    applyRouteQuery()
  },
)

const productFilterLabel = computed(() => {
  if (!productCode.value) return ''
  const fromReview = reviews.value.find((item) => item.productCode === productCode.value)
  if (fromReview) return fromReview.productName
  const fromInquiry = inquiries.value.find((item) => item.productCode === productCode.value)
  return fromInquiry?.productName || ''
})

/** 상품문의(QnA) + 1:1 상담(consult) 을 한 목록으로 병합해 문의 레인에 뿌린다. */
interface InquiryEntry {
  /** ':for=key' 및 drafts 키 (원본 소스별로 유일) */
  key: string
  source: 'qna' | 'consult'
  idx: number
  orderCode: string
  productCode: string
  productName: string
  question: string
  answer: string
  answeredAt: string
  answeredBy: string
  date: string
  author: string
  org: string
  secret: boolean
  responseCount: number
  /**
   * 판매업체가 이 문의에 직접 답변할 수 있는가.
   * - source='qna' : 언제나 true (판매업체 소관)
   * - source='consult' : 관리자가 위임(ccr_seller_reply_allowed='Y') 한 경우에만 true
   */
  sellerCanReply: boolean
  /** 정렬용 원본 생성일 (YYYY-MM-DD HH:mm 또는 YYYY-MM-DD) */
  createdAt: string
}

function toIsoDay(value: string | null | undefined) {
  return String(value || '').slice(0, 10)
}

function toIsoMinute(value: string | Date | null | undefined) {
  if (!value) return ''
  const raw = value instanceof Date ? value.toISOString() : String(value)
  return raw.replace('T', ' ').slice(0, 16)
}

/** 상품문의 → 통합 항목. 판매업체가 직접 답변 가능. */
function qnaToEntry(qna: MallQna): InquiryEntry {
  return {
    key: `qna:${qna.idx}`,
    source: 'qna',
    idx: qna.idx,
    orderCode: String(qna.orderCode || ''),
    productCode: qna.productCode,
    productName: qna.productName,
    question: qna.question,
    answer: qna.answer || '',
    answeredAt: qna.answeredAt || '',
    answeredBy: qna.answer ? '판매업체' : '',
    date: qna.date,
    author: qna.author,
    org: qna.org,
    secret: qna.secret,
    responseCount: qna.answer ? 1 : 0,
    sellerCanReply: true,
    createdAt: qna.date,
  }
}

/**
 * 1:1 상담 → 통합 항목.
 * 기본은 조회 전용이나, 관리자가 위임(consult.sellerReplyAllowed) 한 건은
 * 판매업체가 직접 답변할 수 있다. 이미 관리자 답변이 있는(답변완료) 상담도
 * 위임되면 추가 답변을 남길 수 있다. 위임 여부는 InquiryEntry.sellerCanReply 로 전달한다.
 */
function consultToEntry(consult: SellerSharedConsult): InquiryEntry {
  const question = consult.title
    ? `${consult.title}\n${consult.content}`.trim()
    : consult.content
  return {
    key: `consult:${consult.idx}`,
    source: 'consult',
    idx: consult.idx,
    orderCode: consult.orderCode,
    productCode: '',
    productName: consult.myItems || consult.title || '주문 상담',
    question,
    answer: consult.latestResponse || '',
    answeredAt: toIsoMinute(consult.latestResponseAt) || '',
    answeredBy: consult.latestResponse ? consult.latestResponseBy || '관리자' : '',
    date: toIsoDay(consult.createdAt),
    author: consult.authorId || '',
    org: consult.orgName || '',
    secret: false,
    responseCount: consult.responseCount || 0,
    sellerCanReply: Boolean(consult.sellerReplyAllowed),
    createdAt: toIsoMinute(consult.createdAt) || toIsoDay(consult.createdAt),
  }
}

/** 최신순으로 병합. 원본 생성 시각을 문자열 비교로 정렬한다. */
const mergedInquiries = computed<InquiryEntry[]>(() => {
  const list = [
    ...inquiries.value.map(qnaToEntry),
    ...consults.value.map(consultToEntry),
  ]
  return list.sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0))
})

function clearProductFilter() {
  productCode.value = ''
  const query = { ...route.query }
  delete query.productCode
  delete query.product
  router.replace({ path: '/seller/reviews', query })
}

const laneFilters = [
  { key: 'open' as const, label: '대기' },
  { key: 'done' as const, label: '완료' },
  { key: 'all' as const, label: '전체' },
]

const DEFAULT_REPLY_TEMPLATES = [
  '소중한 후기 감사합니다. 더 좋은 상품으로 보답하겠습니다.',
  '이용해 주셔서 감사합니다. 추가 문의는 언제든 남겨 주세요.',
  '불편을 드려 죄송합니다. 개선하도록 하겠습니다.',
]
const DEFAULT_QNA_TEMPLATES = [
  '문의 감사합니다. 확인 후 안내드리겠습니다.',
  '재고 확인 중입니다. 곧 답변드리겠습니다.',
  '배송은 결제 완료 후 1~3영업일 내 출고됩니다.',
]

const TEMPLATE_KEY = 'seller.reply.templates'

function loadTemplates(): { reply: string[]; qna: string[] } {
  try {
    const raw = localStorage.getItem(TEMPLATE_KEY)
    if (!raw) return { reply: DEFAULT_REPLY_TEMPLATES, qna: DEFAULT_QNA_TEMPLATES }
    const parsed = JSON.parse(raw) as { reply?: string[]; qna?: string[] }
    return {
      reply: Array.isArray(parsed.reply) && parsed.reply.length ? parsed.reply : DEFAULT_REPLY_TEMPLATES,
      qna: Array.isArray(parsed.qna) && parsed.qna.length ? parsed.qna : DEFAULT_QNA_TEMPLATES,
    }
  } catch {
    return { reply: DEFAULT_REPLY_TEMPLATES, qna: DEFAULT_QNA_TEMPLATES }
  }
}

const storedTemplates = loadTemplates()
const replyTemplates = ref(storedTemplates.reply)
const qnaTemplates = ref(storedTemplates.qna)

function applyTemplate(id: string, text: string) {
  const current = (drafts[id] || '').trim()
  drafts[id] = current ? `${current}\n${text}` : text
}

function focusOpenInquiries() {
  mobileTab.value = 'inquiry'
  inquiryFilter.value = 'open'
  focusLane.value = 'inquiry'
}

function focusOpenReviews() {
  mobileTab.value = 'review'
  reviewFilter.value = 'open'
  focusLane.value = 'review'
}

function selectInquiry(id: string) {
  selectedInquiryId.value = id
  focusLane.value = 'inquiry'
}

function selectReview(id: string) {
  selectedReviewId.value = id
  focusLane.value = 'review'
}

async function load() {
  loading.value = true
  errorMessage.value = ''
  try {
    /*
     * 상품문의(QnA)는 판매업체가 직접 답하고, 1:1 상담은 관리자가 답하지만
     * 판매업체도 「내가 판 주문에 걸린 상담」은 참고용으로 함께 보여 준다.
     */
    const [reviewList, qnaList, consultList] = await Promise.all([
      fetchSellerReviews(),
      fetchSellerQnas(),
      fetchSellerConsults().catch(() => [] as SellerSharedConsult[]),
    ])
    reviews.value = reviewList
    inquiries.value = qnaList
    consults.value = consultList
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '목록을 불러오지 못했습니다.'
    reviews.value = []
    inquiries.value = []
    consults.value = []
  } finally {
    loading.value = false
  }
}

onMounted(load)

const unrepliedReviews = computed(() => reviews.value.filter((review) => !review.reply).length)
/*
 * 판매업체가 실제로 답해야 하는 미답변 건 = 상품문의 미답변 + 관리자가 위임한 미답변 1:1 상담.
 * 관리자만 답변하는 (위임 안 된) 1:1 상담은 판매업체 몫이 아니므로 제외한다.
 */
const openInquiries = computed(
  () =>
    inquiries.value.filter((item) => !item.answer).length +
    consults.value.filter((item) => item.sellerReplyAllowed && !item.latestResponse).length,
)

const avgRating = computed(() => {
  if (!reviews.value.length) return '0.0'
  const sum = reviews.value.reduce((acc, review) => acc + review.rating, 0)
  return (sum / reviews.value.length).toFixed(1)
})

const distribution = computed(() =>
  [5, 4, 3, 2, 1].map((score) => {
    const count = reviews.value.filter((review) => review.rating === score).length
    return {
      score,
      count,
      percent: reviews.value.length ? Math.round((count / reviews.value.length) * 100) : 0,
    }
  }),
)

const filteredReviews = computed(() => {
  const query = keyword.value.toLowerCase()
  return reviews.value.filter((review) => {
    if (productCode.value && review.productCode !== productCode.value) return false
    if (!inRange(review.date)) return false
    if (reviewFilter.value === 'open' && review.reply) return false
    if (reviewFilter.value === 'done' && !review.reply) return false
    if (!query) return true
    return (
      review.productName.toLowerCase().includes(query) ||
      review.content.toLowerCase().includes(query) ||
      String(review.orderCode || '')
        .toLowerCase()
        .includes(query)
    )
  })
})

const filteredInquiries = computed(() => {
  const query = keyword.value.toLowerCase()
  return mergedInquiries.value.filter((inquiry) => {
    /*
     * 상품 필터는 상품문의(QnA) 에만 붙는다. 상담은 특정 상품이 아니라 주문 전체가 대상이라 항상 통과.
     * 답변 상태는 소스별로 정의가 다르다.
     *  - 상품문의: mpq_answer 유무
     *  - 1:1 상담: 관리자 응답(res) 유무
     */
    if (productCode.value && inquiry.source === 'qna' && inquiry.productCode !== productCode.value)
      return false
    if (productCode.value && inquiry.source === 'consult') return false
    if (!inRange(inquiry.date)) return false
    const isOpen = !inquiry.answer
    if (inquiryFilter.value === 'open' && !isOpen) return false
    if (inquiryFilter.value === 'done' && isOpen) return false
    if (!query) return true
    return (
      inquiry.productName.toLowerCase().includes(query) ||
      inquiry.question.toLowerCase().includes(query) ||
      String(inquiry.orderCode || '')
        .toLowerCase()
        .includes(query) ||
      inquiry.org.toLowerCase().includes(query)
    )
  })
})

const selectedReview = computed(
  () => filteredReviews.value.find((item) => item.id === selectedReviewId.value) || null,
)
const selectedInquiry = computed(
  () => filteredInquiries.value.find((item) => item.key === selectedInquiryId.value) || null,
)

watch(filteredReviews, (list) => {
  if (!list.length) {
    selectedReviewId.value = ''
    return
  }
  if (!list.some((item) => item.id === selectedReviewId.value)) {
    selectedReviewId.value = list[0].id
  }
})

watch(filteredInquiries, (list) => {
  if (!list.length) {
    selectedInquiryId.value = ''
    return
  }
  if (!list.some((item) => item.key === selectedInquiryId.value)) {
    selectedInquiryId.value = list[0].key
  }
})

function notify(message: string) {
  toast.value = message
  window.clearTimeout(toastTimer)
  toastTimer = setTimeout(() => {
    toast.value = ''
  }, 2400)
}

/*
 * 예전 SLA 뱃지 계산 훅 자리. 현재는 사용하지 않지만 템플릿에서 참조 중이라 빈 값을 돌려주는
 * 스텁으로 남겨 둔다. 미답변 문의의 대기 시간 등을 다시 시각화하고 싶으면 여기서 계산한다.
 */
function inquirySlaClass(_inquiry: InquiryEntry): string {
  return ''
}
function inquirySlaLabel(_inquiry: InquiryEntry): string {
  return ''
}

function canWriteInquiryReply(inquiry: InquiryEntry) {
  if (!inquiry.sellerCanReply) return false
  /* 상품문의는 답변 1회. 1:1 상담은 위임되면 답변완료 후에도 추가 등록 */
  if (inquiry.source === 'qna') return !inquiry.answer
  return true
}

function inquiryReplyPlaceholder(inquiry: InquiryEntry) {
  if (inquiry.source === 'consult') {
    return inquiry.answer
      ? '추가 답변을 작성하세요. 기존 답변 아래에 이어지고, 관리자에게도 공유됩니다.'
      : '상담 답변 내용을 작성하세요. 관리자에게도 함께 공유됩니다.'
  }
  return '문의에 대한 답변을 작성하세요.'
}

/* 템플릿 관리 다이얼로그는 아직 붙지 않았다. 눌렀을 때 안내 토스트만 띄운다. */
function openTemplates() {
  notify('답변 템플릿은 아래 답변란에서 직접 수정해 저장할 수 있습니다.')
}

async function submitReply(review: SellerMallReview) {
  const text = drafts[review.id]?.trim()
  if (!text || working.value) return
  if (!window.confirm('후기 답변을 등록할까요?')) return

  working.value = review.id
  try {
    await replySellerReview(review.idx, text)
    review.reply = text
    review.repliedAt = new Date().toISOString().slice(0, 16).replace('T', ' ')
    drafts[review.id] = ''
    notify('후기 답변을 등록했습니다.')
  } catch (error) {
    notify(error instanceof Error ? error.message : '답변 등록에 실패했습니다.')
  } finally {
    working.value = ''
  }
}

async function submitAnswer(inquiry: InquiryEntry) {
  /* 1:1 상담 중 위임되지 않은 건 판매업체가 답할 수 없다. */
  if (!inquiry.sellerCanReply) {
    notify('1:1 상담 답변은 함우리몰 관리자가 대신 처리합니다.')
    return
  }
  const text = drafts[inquiry.key]?.trim()
  if (!text || working.value) return
  const confirmMessage =
    inquiry.source === 'consult'
      ? inquiry.answer
        ? '이 1:1 상담에 추가 답변을 등록할까요? 기존 답변은 그대로 두고, 관리자에게도 공유됩니다.'
        : '이 1:1 상담에 판매자 답변으로 등록할까요? 관리자에게도 공유됩니다.'
      : '문의 답변을 등록할까요?'
  if (!window.confirm(confirmMessage)) return

  working.value = inquiry.key
  try {
    if (inquiry.source === 'qna') {
      await answerSellerQna(inquiry.idx, text)
      /* 원본 배열도 즉시 갱신해서 mergedInquiries 재계산이 반영되게 한다. */
      const raw = inquiries.value.find((item) => item.idx === inquiry.idx)
      if (raw) {
        raw.answer = text
        raw.answeredAt = new Date().toISOString().slice(0, 16).replace('T', ' ')
      }
    } else {
      await answerSellerConsult(inquiry.idx, text)
      const raw = consults.value.find((item) => item.idx === inquiry.idx)
      if (raw) {
        raw.latestResponse = text
        raw.latestResponseAt = new Date().toISOString()
        raw.latestResponseBy = '판매업체'
        raw.responseCount = (raw.responseCount || 0) + 1
        raw.status = 'DONE'
      }
    }
    drafts[inquiry.key] = ''
    notify(inquiry.source === 'consult' ? '상담 답변을 등록했습니다.' : '문의 답변을 등록했습니다.')
  } catch (error) {
    notify(error instanceof Error ? error.message : '답변 등록에 실패했습니다.')
  } finally {
    working.value = ''
  }
}
</script>

<style lang="scss" src="./SellerReviewsPage.scss"></style>
